import { uniqBy } from "lodash";
import { Airline, StaticData } from "../../../../../core/flight-static-data";
import { ContentGroup } from "../../../../../core/resource/types";
import { formatMessage } from "../../../../../utils";
import {
  AddOnsOptions,
  AirlineSummary,
  BaggageAddOns,
  Facility,
  MealAddOns,
  Segment,
} from "../../types";

type AirlineWithAddOns = Airline & {
  addOns: AddOnsOptions;
  flightCode: string;
};
export default function createAirlines(
  segments: Segment[],
  staticData: StaticData,
  cr: ContentGroup
) {
  const uniqAirlines = uniqBy(segments, "brandAirline").reduce(
    (obj, segment) => {
      const airline = staticData.airlineMap[segment.brandAirline];
      if (!airline) {
        console.error(
          `[createAirlines] Missing airline for brandAirline: ${segment.brandAirline}`
        );
        return obj;
      }

      obj.push({
        ...airline,
        addOns: segment.addOns,
        flightCode: segment.flightCode,
      });
      return obj;
    },
    [] as AirlineWithAddOns[]
  );

  const airlineData = uniqAirlines.map<AirlineSummary>((airline) => {
    const facilities = createFacilities(airline.addOns, cr);
    const hasBaggage = facilities.some(
      (facility) => facility.type === "baggage"
    );
    const hasInFlightMeal = facilities.some(
      (facility) => facility.type === "meal"
    );

    return {
      code: airline.airlineCode,
      flightCode: airline.flightCode,
      image: {
        src: airline.logoUrl,
        alt: airline.airlineName,
      },
      facilities,
      name: airline.airlineName,
      hasBaggage,
      hasInFlightMeal,
    };
  });

  return airlineData;
}

function createFacilities(addOns: AddOnsOptions, cr: ContentGroup): Facility[] {
  // Both addOns is probably an empty array, so index 0 will be undefined
  const [baggageOption] = addOns.baggageOptions;
  const [mealOption] = addOns.mealOptions;

  const baggage = createBaggageAddOns(baggageOption, cr);
  const meal = createMealAddOns(mealOption, cr);

  return [baggage, meal].filter(Boolean) as Facility[];
}

function createBaggageAddOns(
  baggageOption: BaggageAddOns | undefined,
  cr: ContentGroup
) {
  if (!baggageOption) return;
  if (Number(baggageOption.baggageWeight) === 0) {
    return {
      type: "baggage_paid",
      description: formatMessage(cr.baggageUnit || "", {
        num: 0,
      }),
    };
  }

  const { baggageWeight, baggageQuantity, baggageType } = baggageOption;
  const weight = Number(baggageWeight);
  const quantity = Number(baggageQuantity);
  return {
    type: "baggage",
    description:
      baggageType === "PIECE"
        ? formatMessage(cr.baggagePieceUnit || "", {
            weight,
            quantity,
          })
        : formatMessage(cr.baggageUnit || "", {
            num: weight,
          }),
    weight,
    quantity,
  };
}

function createMealAddOns(
  mealOption: MealAddOns | undefined,
  cr: ContentGroup
) {
  if (!mealOption || Number(mealOption.quantity) === 0) return;
  return {
    type: "meal",
    description: cr.inFlightMeal,
  };
}
